// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-270 · LAS PESTAÑAS DE UN MÓDULO Y SU PERMISO — EL ESPEJO DEL NAVEGADOR
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «hay que hacer que almacen tenga permiso individual para cada pestaña, y Seguimientos
//  igual un permiso para cada pestaña interior».
//
//  ESTE FICHERO ES EL ESPEJO de `backend/services/pestanasCatalogo.js`: las mismas claves y las
//  mismas rutas, para que la barra de pestañas, la puerta de ruta y el 403 del servidor no puedan
//  decir cosas distintas. Hay una guardia que compara los dos ficheros clave a clave
//  (`backend/routes/pestanas-permiso-por-pestana.test.js`), igual que con `MODULO_CLAVE`.
//
//  ── LA REGLA, Y POR QUÉ ES `!== false` Y NO `=== true` ────────────────────────────────────────
//  Una pestaña se pinta salvo que su permiso esté DENEGADO. Mientras la respuesta de permisos no ha
//  llegado, el mapa está vacío y `perms[clave]` es `undefined`: eso es «todavía no lo sé», no «no».
//  Con `=== true`, cada F5 dejaría el almacén sin pestañas durante un instante y sacaría de su
//  pantalla a quien estuviera trabajando — el mismo fallo que ya costó una tanda en `PermRoute`.
//  Y como de fábrica la pestaña la tiene quien tiene el módulo (`heredaDe` en el servidor), pintar
//  por defecto es exactamente lo que se ve hoy.
import { t } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export function pestanaAbierta(perms, clave) {
  if (!clave) return true
  return perms?.[clave] !== false
}

// ── ALMACÉN ────────────────────────────────────────────────────────────────────────────────────
// El menú lateral tiene UNA entrada («Almacén») y por dentro se navega entre estas nueve pantallas.
export const PESTANAS_ALMACEN = [
  { clave: 'stock_resumen',       ruta: '/stock',               get label() { return t('services.pestanasModulo.resumen', 'Resumen') }, exacta: true },
  { clave: 'stock_escanear',      ruta: '/stock/escanear',      get label() { return t('services.pestanasModulo.escanear', 'Escanear') } },
  { clave: 'stock_alta_rapida',   ruta: '/stock/alta-rapida',   get label() { return t('services.pestanasModulo.alta_rapida', 'Alta rápida') } },
  { clave: 'stock_compra_rapida', ruta: '/stock/compra-rapida', get label() { return t('services.pestanasModulo.escanear_compra', 'Escanear compra') } },
  { clave: 'stock_productos',     ruta: '/stock/productos',     get label() { return t('services.pestanasModulo.productos', 'Productos') } },
  { clave: 'stock_proveedores',   ruta: '/stock/proveedores',   get label() { return t('services.pestanasModulo.proveedores', 'Proveedores') } },
  { clave: 'stock_ubicaciones',   ruta: '/stock/ubicaciones',   get label() { return t('services.pestanasModulo.ubicaciones', 'Ubicaciones') } },
  { clave: 'stock_lista_compra',  ruta: '/stock/lista-compra',  get label() { return t('services.pestanasModulo.lista_de_compra', 'Lista de compra') } },
  { clave: 'stock_reutilizables', ruta: '/stock/reutilizables', get label() { return t('services.pestanasModulo.reutilizables', 'Reutilizables') } },
]

// ── SEGUIMIENTO ────────────────────────────────────────────────────────────────────────────────
// Lista de espera y Encuestas NO estrenan clave: usan la suya de toda la vida, que además gobierna
// su router entero en el servidor. Dos casillas para lo mismo en la matriz sería peor.
export const PESTANAS_SEGUIMIENTO = [
  { clave: 'seg_recordatorios', ruta: '/recordatorios' },
  { clave: 'seg_recalls',       ruta: '/recalls' },
  { clave: 'lista_espera',      ruta: '/lista-espera' },
  { clave: 'seg_anuladas',      ruta: '/citas-anuladas' },
  { clave: 'encuestas',         ruta: '/encuestas' },
]

// Ruta de pestaña → clave de permiso (las dos listas juntas).
export const PESTANA_CLAVE = Object.fromEntries(
  [...PESTANAS_ALMACEN, ...PESTANAS_SEGUIMIENTO].map((p) => [p.ruta, p.clave]),
)

// La primera pestaña del almacén que esta persona SÍ tiene, o null si no le queda ninguna. La usa
// la puerta de ruta para no dejar a nadie en una pantalla que no puede ver, y la pantalla de «no
// tienes ninguna pestaña» para saber que no hay a dónde mandarle.
export function primeraPestanaAlmacen(perms) {
  const p = PESTANAS_ALMACEN.find((x) => pestanaAbierta(perms, x.clave))
  return p ? p.ruta : null
}


// ── LA CASILLA DE LA MATRIZ DE PERMISOS (Ajustes → Usuarios, doctores y permisos) ────────────────────────
//
//  ESTO ARREGLA UNA PANTALLA QUE MENTIA. La matriz pintaba cada casilla con
//  `c.rolesDefault.includes(rol)`, y el `rolesDefault` de una pestaña es una COPIA del de su módulo
//  —está ahí para que la fila se pueda pintar, no para decidir—. Resultado: a una recepcionista con
//  «Almacén / stock» concedido A MANO, el servidor le abría las nueve pestañas y la matriz se las
//  enseñaba las nueve DESMARCADAS. El administrador leía «no tiene Proveedores» de alguien que sí
//  entraba a Proveedores.
//
//  Aquí se repite la MISMA cadena que resuelve el servidor en `services/permisos · puede()`:
//  override primero, luego lo que herede de su módulo, y solo al final el defecto del rol.
//
//  `dameOverride` es una función y no un objeto a propósito: las dos matrices guardan sus overrides
//  con claves distintas (la de roles, `rol|clave`; la de una persona, `clave`), y con la función
//  cada una pasa la suya sin que esta regla tenga que conocer las dos.
//
//  LO QUE ESTA REGLA NO PUEDE SABER, dicho aquí para que nadie lo confunda con un fallo: la matriz
//  de UNA PERSONA solo recibe los overrides de esa persona, no los de su rol. Si la clínica
//  concedió el módulo al ROL, esa pantalla lo pinta según el defecto del rol — como hacía antes de
//  esto y con módulos y pestañas por igual. Una pestaña nunca se pinta peor que su módulo.
//
//  B-1916 · `persona` ({ esDoctor }, solo en la matriz de UNA persona): la casilla «de fábrica, solo los doctores»
//  (`soloDoctores`, las prescripciones por voz) se pinta con lo que el servidor dice de esa persona. En la de roles no hay
//  persona: se pinta lo del rol, y su etiqueta ya dice que de ese rol solo la tienen los doctores.
export function casillaMarcada(porClave, dameOverride, rolBase, clave, persona) {
  const o = dameOverride(clave)
  if (o !== undefined) return o === true
  const c = porClave[clave]
  if (!c) return false
  if (c.heredaDe) return casillaMarcada(porClave, dameOverride, rolBase, c.heredaDe, persona)
  if (c.soloDoctores && rolBase !== 'admin' && persona && persona.esDoctor === false) return false
  return (c.rolesDefault || []).includes(rolBase)
}
